import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import type { UserProfileDraft } from "@/modules/users/entities/usersEntity"
import { type AddressInputKey } from "@modules/address/addressFormikType"
import { word } from "@modules/core/utils/i18n"
import { GenderCategory } from "@modules/profile/publicProfile/gateway/publicProfileDto"
import { defaultDateFormat } from "@modules/utils/dateUtils"
import { nameRegExp, usernameRegExp } from "@modules/utils/regexp"
import dayjs from "dayjs"
import * as Yup from "yup"

export type ProfileRegisterFormikType = {
  firstname: string
  lastname: string
  username: string
  birthdate?: string
  birthdateVisibility?: boolean
  genderCategory?: GenderCategory
  genderCategoryVisibility?: boolean
  nationality?: string
  nationalityVisibility?: boolean
  avatar?: MediaItemFormType
}

export type ProfileRegisterInputKey =
  | "firstname"
  | "lastname"
  | "username"
  | "avatar"
  | "birthdate"
  | "genderCategory"
  | "nationality"
  | AddressInputKey

export function convertProfileToFormik(userDraft: UserProfileDraft): ProfileRegisterFormikType {
  return {
    firstname: userDraft.firstname,
    lastname: userDraft.lastname,
    username: userDraft.username,
    birthdate: userDraft.birthdate?.value,
    birthdateVisibility: userDraft.birthdate?.visibility.all,
    genderCategory: userDraft.genderCategory?.value,
    genderCategoryVisibility: userDraft.genderCategory?.visibility.all,
    nationality: userDraft.nationality?.value,
    nationalityVisibility: userDraft.nationality?.visibility.all,
  }
}

export function convertProfileFormFormik(formik: ProfileRegisterFormikType): Omit<UserProfileDraft, "address"> {
  return {
    firstname: formik.firstname,
    lastname: formik.lastname,
    username: formik.username,
    imageKey: formik.avatar?.key ?? undefined,
    birthdate: formik.birthdate
      ? {
          value: dayjs(formik.birthdate).format(defaultDateFormat),
          visibility: { all: formik.birthdateVisibility ?? true },
        }
      : undefined,
    genderCategory: formik.genderCategory
      ? {
          value: formik.genderCategory ?? null,
          visibility: {
            all: formik.genderCategoryVisibility ?? true,
          },
        }
      : undefined,
    nationality: formik.nationality
      ? {
          value: formik.nationality ?? null,
          visibility: {
            all: formik.nationalityVisibility ?? true,
          },
        }
      : undefined,
  }
}

const firstPageValidation = () =>
  Yup.object({
    firstname: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .matches(nameRegExp, word("form.invalid.firstname"))
      .required(word("form.required.firstname")),
    lastname: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .matches(nameRegExp, word("form.invalid.lastname"))
      .required(word("form.required.lastname")),
  })

const secondPageValidation = () =>
  Yup.object({
    genderCategory: Yup.string().required(),
  })

const thirdPageValidation = () =>
  Yup.object({
    birthdate: Yup.string().required(word("form.required.birthDate")),
    nationality: Yup.string().required(word("form.required.nationality")),
  })

const fourthPageValidation = () =>
  Yup.object({
    username: Yup.string()
      .max(64, word("form.maximum.characters", { count: 64 }))
      .matches(usernameRegExp, word("form.invalid.username"))
      .required(word("form.required.username")),
  })

export function getValidationSchema(pageNumber?: number) {
  switch (pageNumber) {
    case 1:
      return firstPageValidation()
    case 2:
      return secondPageValidation()
    case 3:
      return thirdPageValidation()
    case 4:
      return fourthPageValidation()
    default:
      return firstPageValidation().concat(secondPageValidation())
  }
}
